import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { SkText } from '../../components/SkeletonLoader/SkeletonLoader';
import { fetchRastreioPedidos, fetchRastreioEstatisticas, fetchTransportadoras, RastreioPedido, RastreioEstatisticas } from './rastreioAPI';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import { useAuthStore } from '../../stores/authStore';
import { CarteiraSelector } from '../../components/CarteiraSelector/CarteiraSelector';
import CustomSelect from '../../components/CustomSelect';
import {
    DateNavigator,
    formatLocalDate,
    getYearRange,
    getWeekRange,
} from '../../components/DateNavigator/DateNavigator';
import navStyles from '../../components/DateNavigator/DateNavigator.module.css';
import styles from './RastreioPage.module.css';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BarChartIcon from '@mui/icons-material/BarChart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InventoryIcon from '@mui/icons-material/Inventory';


// ──── Skeleton ────
const COL_WIDTHS = [110, 60, 90, 90, 110, 80, 180, 140, 40, 100, 100];

const RastreioSkeleton: React.FC = () => (
    <div style={{ width: '100%' }}>
        <div style={{ background: 'var(--bg-widget)', borderRadius: 'var(--radius-base, 16px)', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)', border: '1px solid var(--border-primary)' }}>
            {Array.from({ length: 12 }).map((_, rowIdx) => (
                <div key={rowIdx} style={{ display: 'flex', gap: '16px', padding: '14px 16px', borderBottom: '1px solid var(--border-primary)', alignItems: 'center' }}>
                    {COL_WIDTHS.map((w, i) => (
                        <SkText key={i} width={i === 0 ? 90 : w} height={12} style={{ flexShrink: 0 }} />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

const HighlightText: React.FC<{ text: string; highlight: string }> = ({ text, highlight }) => {
    if (!highlight.trim() || typeof text !== 'string') {
        return <span>{text}</span>;
    }
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);
    return (
        <span>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <span key={i} className={styles.highlight}>{part}</span>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </span>
    );
};

// Cache em memória local para evitar Skeleton ao trocar de aba
let moduleCachedPedidos: RastreioPedido[] | null = null;
let moduleCachedStats: RastreioEstatisticas | null = null;
let moduleCachedTransportadoras: string[] = [];
let moduleCachedTotalPages: number = 1;
let moduleCachedTotalItems: number = 0;

const RastreioPage: React.FC = () => {
    const isSupervisor = useAuthStore((state) => state.role === 'supervisor' || state.role === 'diretor');
    // DateNavigator state (same as ProdutosPage)
    const [periodo, setPeriodo] = useState<'anual' | 'mensal' | 'semanal' | 'custom'>('mensal');
    const [dataRef, setDataRef] = useState<string>(() => formatLocalDate(new Date()));
    const [dataInicio, setDataInicio] = useState<string>('');
    const [dataFim, setDataFim] = useState<string>('');

    /** Retorna o período fiscal mensal: dia 26 do mês anterior ao dia 25 do mês atual.
     *  dataRef é uma string 'YYYY-MM-DD' usada como referência de mês/ano. */
    const getFiscalMonthRange = (dateRef: string): { start: Date; end: Date } => {
        const d = new Date(dateRef + 'T12:00:00');
        const year = d.getFullYear();
        const month = d.getMonth(); // 0-indexed
        // período: 26 do mês anterior → 25 do mês de referência
        return {
            start: new Date(year, month - 1, 26, 0, 0, 0, 0),
            end: new Date(year, month, 25, 23, 59, 59, 999),
        };
    };

    const { startDate, endDate } = useMemo(() => {
        if (periodo === 'custom') {
            return { startDate: dataInicio, endDate: dataFim };
        }
        const dRef = dataRef || formatLocalDate(new Date());
        let range: { start: Date; end: Date };
        if (periodo === 'mensal') range = getFiscalMonthRange(dRef);
        else if (periodo === 'semanal') range = getWeekRange(dRef);
        else range = getYearRange(dRef);
        return {
            startDate: formatLocalDate(range.start),
            endDate: formatLocalDate(range.end),
        };
    }, [periodo, dataRef, dataInicio, dataFim]);


    const [pedidos, setPedidos] = useState<RastreioPedido[]>(moduleCachedPedidos || []);
    const [stats, setStats] = useState<RastreioEstatisticas | null>(moduleCachedStats);
    const [transportadoras, setTransportadoras] = useState<string[]>(moduleCachedTransportadoras);

    const [loading, setLoading] = useState<boolean>(!moduleCachedPedidos);
    const [loadingStats, setLoadingStats] = useState<boolean>(!moduleCachedStats);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(moduleCachedTotalPages);
    const [totalItems, setTotalItems] = useState<number>(moduleCachedTotalItems);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [selectedTransportadora, setSelectedTransportadora] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const itemsPerPage = 30;

    const loadData = useCallback(async (page: number) => {
        if (!startDate || !endDate) return;
        try {
            setLoading(true);
            const response = await fetchRastreioPedidos(
                startDate,
                endDate,
                page,
                itemsPerPage,
                debouncedSearchTerm,
                selectedStatus,
                selectedTransportadora
            );
            setPedidos(response.data);
            moduleCachedPedidos = response.data;
            setTotalPages(response.totalPages);
            moduleCachedTotalPages = response.totalPages;
            setTotalItems(response.total);
            moduleCachedTotalItems = response.total;
            setError(null);
        } catch (err) {
            setError('Falha ao carregar os pedidos de rastreio.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, debouncedSearchTerm, selectedStatus, selectedTransportadora]);

    useEffect(() => {
        const loadStats = async () => {
            if (!startDate || !endDate) return;
            try {
                if (!moduleCachedStats) {
                    setLoadingStats(true);
                }
                const response = await fetchRastreioEstatisticas(startDate, endDate, selectedTransportadora);
                setStats(response);
                moduleCachedStats = response;
            } catch (err) {
                console.error("Falha ao carregar estat\u00edsticas", err);
            } finally {
                setLoadingStats(false);
            }
        };
        loadStats();
    }, [startDate, endDate, selectedTransportadora, refreshKey]);

    useEffect(() => {
        const loadTransportadoras = async () => {
            if (!startDate || !endDate) return;
            try {
                const response = await fetchTransportadoras(startDate, endDate);
                setTransportadoras(response);
                moduleCachedTransportadoras = response;
            } catch (err) {
                console.error("Falha ao carregar transportadoras", err);
            }
        };
        loadTransportadoras();
    }, [startDate, endDate]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        loadData(currentPage);
    }, [loadData, currentPage, refreshKey]);

    // Auto-refresh a cada 5 minutos
    useEffect(() => {
        const intervalId = setInterval(() => {
            setRefreshKey(prev => prev + 1);
        }, 300000);
        return () => clearInterval(intervalId);
    }, []);

    const paginate = (pageNumber: number) => {
        if (pageNumber !== currentPage) {
            setCurrentPage(pageNumber);
        }
    };

    const handlePreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
    const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };

    const getPaginationButtons = () => {
        const pageNumbers: (number | string)[] = [];
        const maxButtons = 5;
        const halfMax = Math.floor(maxButtons / 2);

        if (totalPages <= maxButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            if (currentPage > halfMax + 2) pageNumbers.push('...');
            let startPage = Math.max(2, currentPage - halfMax);
            let endPage = Math.min(totalPages - 1, currentPage + halfMax);
            if (currentPage <= halfMax + 1) endPage = maxButtons - 1;
            if (currentPage >= totalPages - halfMax) startPage = totalPages - maxButtons + 2;
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (currentPage < totalPages - halfMax - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.filterBar}>
                <div className={styles.filterTopRow}>
                    <div className={styles.filterGroup}>
                        <div className={navStyles.periodContainer}>
                            {(['anual', 'mensal', 'semanal', 'custom'] as const).map((p) => (
                                <button
                                    key={p}
                                    onClick={() => {
                                        setPeriodo(p);
                                        setCurrentPage(1);
                                    }}
                                    className={`${navStyles.periodButton} ${periodo === p ? navStyles.periodButtonActive : ''}`}
                                >
                                    {p === 'custom' ? 'Personalizado' : p}
                                </button>
                            ))}
                        </div>

                        <DateNavigator
                            label=""
                            periodo={periodo}
                            dataRef={dataRef}
                            dataInicio={dataInicio}
                            dataFim={dataFim}
                            isFiscal={true}
                            onChange={(vals) => {
                                if (vals.periodo) setPeriodo(vals.periodo);
                                if (vals.dataRef) setDataRef(vals.dataRef);
                                if (vals.dataInicio) setDataInicio(vals.dataInicio);
                                if (vals.dataFim) setDataFim(vals.dataFim);
                                setCurrentPage(1);
                            }}
                        />
                    </div>

                    <ModernDashboardHeader variant="minimal" />
                </div>
                {!loadingStats && stats && (
                    <div className={styles.kpiBottomRow}>
                        <span className={styles.kpiMicroItem} style={{ cursor: 'pointer' }} title="Limpar filtro" onClick={() => { setSelectedStatus(''); setCurrentPage(1); }}>
                            <BarChartIcon style={{ fontSize: 16, color: '#64748b' }} />
                            <span className={styles.kpiMicroLabel}>Total:</span>
                            <span className={styles.kpiMicroValue}>{stats.total}</span>
                        </span>
                        <span className={styles.kpiMicroItem} style={{ cursor: 'pointer' }} title="Filtrar Entregues" onClick={() => { setSelectedStatus(prev => prev === 'entregue' ? '' : 'entregue'); setCurrentPage(1); }}>
                            <CheckCircleOutlineIcon style={{ fontSize: 16, color: '#16a34a' }} />
                            <span className={styles.kpiMicroLabel}>Entregues:</span>
                            <span className={styles.kpiMicroValue}>{stats.entregues}</span>
                        </span>
                        <span className={styles.kpiMicroItem} style={{ cursor: 'pointer' }} title="Filtrar Em Separação" onClick={() => { setSelectedStatus(prev => prev === 'em-separacao' ? '' : 'em-separacao'); setCurrentPage(1); }}>
                            <InventoryIcon style={{ fontSize: 16, color: '#8b5cf6' }} />
                            <span className={styles.kpiMicroLabel}>Em Separação:</span>
                            <span className={styles.kpiMicroValue}>{stats.emSeparacao}</span>
                        </span>
                        <span className={styles.kpiMicroItem} style={{ cursor: 'pointer' }} title="Filtrar No Prazo" onClick={() => { setSelectedStatus(prev => prev === 'dentro-do-prazo' ? '' : 'dentro-do-prazo'); setCurrentPage(1); }}>
                            <AccessTimeIcon style={{ fontSize: 16, color: '#2563eb' }} />
                            <span className={styles.kpiMicroLabel}>No Prazo:</span>
                            <span className={styles.kpiMicroValue}>{stats.dentroDoPrazo}</span>
                        </span>
                        <span className={styles.kpiMicroItem} style={{ cursor: 'pointer' }} title="Filtrar Em Viagem" onClick={() => { setSelectedStatus(prev => prev === 'em-viagem' ? '' : 'em-viagem'); setCurrentPage(1); }}>
                            <LocalShippingIcon style={{ fontSize: 16, color: '#0369a1' }} />
                            <span className={styles.kpiMicroLabel}>Em Viagem:</span>
                            <span className={styles.kpiMicroValue}>{stats.emViagem}</span>
                        </span>
                        <span className={styles.kpiMicroItem} style={{ cursor: 'pointer' }} title="Filtrar Atrasados" onClick={() => { setSelectedStatus(prev => prev === 'atrasado' ? '' : 'atrasado'); setCurrentPage(1); }}>
                            <ErrorOutlineIcon style={{ fontSize: 16, color: '#dc2626' }} />
                            <span className={styles.kpiMicroLabel}>Atrasados:</span>
                            <span className={styles.kpiMicroValue}>{stats.atrasados}</span>
                        </span>
                        <span className={styles.kpiMicroItem} style={{ cursor: 'pointer' }} title="Filtrar Atraso Carregamento" onClick={() => { setSelectedStatus(prev => prev === 'atraso-carregamento' ? '' : 'atraso-carregamento'); setCurrentPage(1); }}>
                            <WarningAmberIcon style={{ fontSize: 16, color: '#ea580c' }} />
                            <span className={styles.kpiMicroLabel}>Atraso Carregamento:</span>
                            <span className={styles.kpiMicroValue}>{stats.atrasoCarregamento}</span>
                        </span>
                    </div>
                )}
            </div>

            <div className={styles.tableContainer}>
                <div className={styles.tableToolbar}>
                    <div className={styles.tableSearchBar}>
                        <SearchIcon className={styles.tableSearchIcon} />
                        <input
                            type="text"
                            placeholder="Buscar por NF, cliente ou rastreio..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.tableSearchInput}
                        />
                        {searchTerm && (
                            <ClearIcon
                                className={styles.tableSearchClear}
                                onClick={() => setSearchTerm('')}
                            />
                        )}
                    </div>

                    <div className={styles.tableFilters}>
                        {isSupervisor && <CarteiraSelector />}
                        <CustomSelect
                            id="transportadora-select" label="Transportadora"
                            value={selectedTransportadora}
                            onChange={(e) => { setSelectedTransportadora(e.target.value); setCurrentPage(1); }}
                            placeholder="Transportadora"
                            options={[{ value: '', label: 'Todas' }, ...transportadoras.map((t) => ({ value: t, label: t }))]}
                        />
                        <CustomSelect
                            id="status-select" label="Status"
                            value={selectedStatus}
                            onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                            placeholder="Status"
                            options={[
                                { value: '', label: 'Todos' },
                                { value: 'entregue', label: 'Entregue' },
                                { value: 'dentro-do-prazo', label: 'Dentro do Prazo' },
                                { value: 'em-separacao', label: 'Em Separação' },
                                { value: 'em-viagem', label: 'Em Viagem' },
                                { value: 'atrasado', label: 'Atrasado' },
                                { value: 'atraso-carregamento', label: 'Atraso Carregamento' },
                                { value: 'agendado', label: 'Agendado' },
                                { value: 'indisponivel', label: 'Indisp. Rastreio' }
                            ]}
                        />
                    </div>
                </div>
                {loading ? (
                    <>
                        <table className={styles.ordersTable}>
                            <thead>
                                <tr>
                                    <th>
                                        Status{' '}
                                        <span
                                            className={styles.statusHelpIcon}
                                            title={[
                                                '✅ Entregue — o pedido já chegou ao cliente.',
                                                '🟢 Dentro do Prazo — ainda aguardando sair do estoque.',
                                                '🟣 Em Separação — o pedido foi separado e está aguardando carregamento.',
                                                '🚚 Em Viagem — o pedido já foi carregado e está a caminho.',
                                                '🔴 Atrasado — passou do prazo e ainda não foi entregue.',
                                                '🟠 Atraso Carregamento — nota saiu há mais de 3 dias úteis mas o pedido ainda não foi carregado.',
                                                '🟡 Agendado — entrega marcada para uma data específica.',
                                                '⚪ Indisp. Rastreio — pedido entregue pelo próprio motorista da empresa (sem rastreio externo).',
                                            ].join('\n')}
                                        >
                                            ?
                                        </span>
                                    </th>
                                    <th style={{ textAlign: 'center' }}>
                                        Entrega Prevista{' '}
                                        <span
                                            className={styles.statusHelpIcon}
                                            title="Data prevista de entrega calculada a partir do carregamento + prazo em dias úteis."
                                        >
                                            ?
                                        </span>
                                    </th>
                                    <th>Ped. Produção</th>
                                    <th>Ped. Comercial</th>
                                    <th>Data Nota</th>
                                    <th>Carregamento</th>
                                    <th>Nº Nota</th>
                                    <th>Cliente</th>
                                    <th>Transportadora</th>
                                    <th>UF</th>
                                    <th>Cidade</th>
                                    <th>Cód. Rastreio</th>
                                </tr>
                            </thead>
                        </table>
                        <RastreioSkeleton />
                    </>
                ) : error ? (
                    <p className={styles.error}>{error}</p>
                ) : (
                    <table className={styles.ordersTable}>
                        <thead>
                            <tr>
                                <th>
                                    Status{' '}
                                    <span
                                        className={styles.statusHelpIcon}
                                        title={[
                                            '✅ Entregue — o pedido já chegou ao cliente.',
                                            '🟢 Dentro do Prazo — ainda aguardando sair do estoque.',
                                            '🟣 Em Separação — o pedido foi separado e está aguardando carregamento.',
                                            '🚚 Em Viagem — o pedido já foi carregado e está a caminho.',
                                            '🔴 Atrasado — passou do prazo e ainda não foi entregue.',
                                            '🟠 Atraso Carregamento — nota saiu há mais de 3 dias úteis mas o pedido ainda não foi carregado.',
                                            '🟡 Agendado — entrega marcada para uma data específica.',
                                            '⚪ Indisp. Rastreio — pedido entregue pelo próprio motorista da empresa (sem rastreio externo).',
                                        ].join('\n')}
                                    >
                                        ?
                                    </span>
                                </th>
                                <th style={{ textAlign: 'center' }}>
                                    Entrega Prevista{' '}
                                    <span
                                        className={styles.statusHelpIcon}
                                        title="Data prevista de entrega calculada a partir do carregamento + prazo em dias úteis."
                                    >
                                        ?
                                    </span>
                                </th>
                                <th>Ped. Produção</th>
                                <th>Ped. Comercial</th>
                                <th>Data Nota</th>
                                <th>Carregamento</th>
                                <th>Nº Nota</th>
                                <th>Cliente</th>
                                <th>Transportadora</th>
                                <th>UF</th>
                                <th>Cidade</th>
                                <th>Cód. Rastreio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pedidos.map((pedido: RastreioPedido) => {
                                const dataNota = pedido.dataNota ? new Date(pedido.dataNota).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-';
                                const dataCarregamento = pedido.dataCarregamentoReal ? new Date(pedido.dataCarregamentoReal).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-';
                                const dataPrevisao = pedido.previsaoEntrega ? new Date(pedido.previsaoEntrega).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-';

                                let statusClass = '';
                                let statusIcon = null;
                                if (pedido.status === 'entregue') {
                                    statusClass = styles.statusEntregue;
                                    statusIcon = <CheckCircleOutlineIcon fontSize="inherit" />;
                                } else if (pedido.status === 'dentro-do-prazo') {
                                    statusClass = styles.statusDentroDoPrazo;
                                    statusIcon = <AccessTimeIcon fontSize="inherit" />;
                                } else if (pedido.status === 'em-separacao') {
                                    statusClass = styles.statusEmSeparacao;
                                    statusIcon = <InventoryIcon fontSize="inherit" />;
                                } else if (pedido.status === 'atrasado') {
                                    statusClass = styles.statusAtrasado;
                                    statusIcon = <ErrorOutlineIcon fontSize="inherit" />;
                                } else if (pedido.status === 'atraso-carregamento') {
                                    statusClass = styles.statusAtrasoCarregamento;
                                    statusIcon = <ErrorOutlineIcon fontSize="inherit" />;
                                } else if (pedido.status === 'em-viagem') {
                                    statusClass = styles.statusEmViagem;
                                    statusIcon = <LocalShippingIcon fontSize="inherit" />;
                                } else if (pedido.status === 'agendado') {
                                    statusClass = styles.statusAgendado;
                                    statusIcon = <AccessTimeIcon fontSize="inherit" />;
                                } else if (pedido.status === 'indisponivel') {
                                    statusClass = styles.statusIndisponivel;
                                    statusIcon = null;
                                }

                                return (
                                    <tr key={pedido.id} className={styles.tableRow}>
                                        <td>
                                            <span className={`${styles.statusBadge} ${statusClass}`}>
                                                {statusIcon} {pedido.statusText}
                                            </span>
                                        </td>
                                        <td style={{ color: '#475569', fontWeight: 500, textAlign: 'center' }}>
                                            {(pedido.status === 'em-viagem' || pedido.status === 'atrasado') && pedido.previsaoEntrega
                                                ? new Date(pedido.previsaoEntrega).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                                                : '-'}
                                        </td>
                                        <td className={styles.pedProducao}><HighlightText text={pedido.pedidoProducao || '---'} highlight={debouncedSearchTerm} /></td>
                                        <td className={styles.pedComercial}>{pedido.pedidoComercial}</td>
                                        <td>{dataNota}</td>
                                        <td>{dataCarregamento}</td>
                                        <td><HighlightText text={pedido.numeroNota || '---'} highlight={debouncedSearchTerm} /></td>
                                        <td className={styles.clientCell} title={pedido.cliente}>
                                            <HighlightText text={pedido.cliente} highlight={debouncedSearchTerm} />
                                        </td>
                                        <td>{pedido.transportadora || '-'}</td>
                                        <td>{pedido.ufEntrega || '-'}</td>
                                        <td>{pedido.cidadeEntrega || '-'}</td>
                                        <td><HighlightText text={pedido.codigoRastreio || '-'} highlight={debouncedSearchTerm} /></td>
                                    </tr>
                                );
                            })}
                            {pedidos.length === 0 && (
                                <tr>
                                    <td colSpan={12} className={styles.emptyTable}>Nenhum pedido encontrado.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {!loading && !error && totalPages > 0 && (
                <div className={styles.paginationContainer}>
                    <div className={styles.paginationInfo}>
                        Exibindo <strong>{pedidos.length}</strong> de <strong>{totalItems}</strong> pedidos (Página {currentPage})
                    </div>
                    <div className={styles.pageButtons}>
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className={`${styles.paginationButton} ${styles.paginationButtonText}`}
                        >
                            <ChevronLeftIcon fontSize="small" />
                            Anterior
                        </button>
                        {getPaginationButtons().map((item, index) => (
                            item === '...' ?
                                <span key={index} className={styles.paginationEllipsis}>...</span> :
                                <button
                                    key={index}
                                    onClick={() => paginate(item as number)}
                                    className={`${styles.paginationButton} ${item === currentPage ? styles.paginationActiveButton : ''}`}
                                >
                                    {item}
                                </button>
                        ))}
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className={`${styles.paginationButton} ${styles.paginationButtonText}`}
                        >
                            Próxima
                            <ChevronRightIcon fontSize="small" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RastreioPage;
